import React, { useRef, useEffect } from "react";
import { injectIntl, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
// Components
import { Grid, Grow, Container, Typography } from "@mui/material";
import { Table, SearchInput, Toolbar, Button } from 'Components/uiControls';
import { NotificationManager } from 'Components/Notifications';
import { TableButtons } from 'Components/uiControls/Table';
import { MenuComponent } from 'Components/Menu';
import { usePagination } from 'Components/uiControls/Table/hooks';
import { HasPermission } from "Core/security"
import "./styles.scss";
// Actions
import { 
  searchContacts,
  sendGenericExport,
  clearRedux
} from "Redux/actions";

const TableContacts = (props) => {
  // Props
  const {
    onActions
  } = props;
  
  // Intl
  const { messages, locale } = useIntl();

  // State manager
  const { 
    registers,
    loading, 
    allfilters,
    total,
    deleteContactResponse
  } = useSelector(
    (state) => state.contactsRedux
  );

  const exportResult = useSelector(state => state.commomRedux.exportResult);
  const exportName = useSelector(state => state.commomRedux.exportName);

  // Permissions
  const user = useSelector((state) => state.securityRedux.user);
  const editablePermissions = HasPermission(user, "", [332]);

  const dispatch = useDispatch();

  // Ref
  const tableRef = useRef();
  const pagination = usePagination(tableRef, searchContacts, allfilters);

  const labelContactType = (label) => {
    if(label === "email"){
      return messages['accountContacts_email']
    }
    if(label === 'cellphone'){
      return messages['accountContacts_cellphone']
    }
  }
 
  useEffect(() => {
    if (total) {
      pagination.setTotal(total);
    }
  }, [total]);

  useEffect(() => {
    pagination.setColumns([
        {
            key: 'title',
            title: messages['accountContacts_title'],
            show: true,
            export: true,
            render: (row) => {
              const options = [messages['edit'], messages['delete']];
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="flex-start"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item zeroMinWidth>
                          <Typography noWrap>{row?.title}</Typography>
                        </Grid>
                        {editablePermissions && 
                          <Grid item>
                            <MenuComponent
                              disabled={loading}
                              options={options}
                              icon={'more_vert'}
                              className="context-menu"
                              onClick={(type, value) => onActions(type, value)}
                              value={row}
                            />
                          </Grid>
                        }
                    </Grid>
                )
            },
            exportValue: (row) => row?.title,
            functionString: 'return row?.title'
        },
        {
            key: 'type',
            title: messages['accountContacts_details'],
            show: true,
            export: true,
            render: (row) => {
                return <span>{labelContactType(row.type)}</span>
            },
            exportValue: (row) => labelContactType(row.type),
            functionString: 'return row?.type'
        }
    ]);
    pagination.onChangePage();
  }, [locale]);

  useEffect(() => {
    if (exportResult && exportName === "contacts") {
      NotificationManager.info(
        messages['exportInProcessBody'],
        messages['exportInProcessTitle'],
        4000,
        null,
        null,
        'info',
        null
      );
      dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
    }
  }, [exportResult, exportName]);

  useEffect(() => {
    if (deleteContactResponse) {
      NotificationManager.success(
        messages['accountContacts_deleteContact'],
        messages['success'],
        3000,
        null,
        null,
        'Success',
      );
      pagination?.onSearchText("")
      dispatch(
        searchContacts({
          ...allfilters,
          openSearch: ''
        })
      );
    }
  }, [deleteContactResponse]);

  return (
    <Container maxWidth={false}>
      <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <Grid item xs={6} sm={2}>
              <Toolbar>
                  <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    {editablePermissions && 
                      <Grid item>
                        <Button
                          type="icon"
                          icon="add_box"
                          placement="bottom"
                          tooltip={messages['add']}
                          onClick={() => onActions('Create')}
                        />
                      </Grid>
                    }
                    <Grid item>
                      <TableButtons 
                        messages={messages} 
                        tableRef={tableRef} 
                        {...pagination} 
                        buttons={['export']} 
                        onExport={(type) => {
                          if (pagination?.selectedItems?.length) {
                              if (type === "Csv") {
                                tableRef.current.exportCSV();
                              } else if (type === "Pdf") {
                                tableRef.current.exportPDF();
                              }
                          } else {
                            const columns = tableRef.current?.columns
                            const headers = columns.filter(x => x.show && x.export).map(x => { return { header: x.title, column: x.key, functionString: x.functionString } }) || [];

                            dispatch(sendGenericExport(
                              "contacts",
                              type,
                              `
                              query getAllContacts ($openSearch: String, $limit: Int!, $offset: Int) {
                                contacts: getAllContacts (openSearch: $openSearch, limit: $limit, offset: $offset) {
                                  items {
                                    id
                                    title
                                    address
                                    type
                                  }
                                  total
                                }
                              }
                              `,
                              { openSearch: pagination?.searchText },
                              headers,
                              { unit: 'pt', size: 'A4', orientation: 'portrait', fontSize: 10 }
                            ));
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
              </Toolbar>
          </Grid>
          <Grid item xs={12} sm={4} className="mb-10 text-right">
              <SearchInput
                placeholder={messages['search']}
                {...pagination}
              />
          </Grid>
      </Grid>
      <Table
        ref={tableRef}
        keyColumn="title"
        loading={loading}
        topheight={235}
        data={registers}
        exportOrientation='portrait'
        exportName='account-contacts'
        {...pagination}
        hideDragable
        disableItemSelection
      />  
    </Container>
    
  );
};

export default injectIntl(TableContacts);
